import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useState } from "react";

import { AvailableIntegrationFragment } from "@/apollo/types";
import { useCallbackRef } from "@/hooks/useCallbackRef";
import { useIsMobileSizeViewport } from "@/hooks/useIsMobileSizeViewport";
import { useIntegrationDocumentationResource } from "@/integrations";
import { createStorageKey } from "@/utils/storage";

const defaultShowAtom = atomWithStorage<boolean>(
  createStorageKey("default-show-documentation-pane"),
  true,
);

export function useDocumentationPane(
  integration:
    Pick<AvailableIntegrationFragment, "id" | "aliasMetadata"> | undefined,
  options: {
    onClose?: () => void;
    onOpen?: () => void;
  },
) {
  const isMobileSizeViewport = useIsMobileSizeViewport();
  const [defaultShowDocumentation, setDefaultShowDocumentation] =
    useAtom(defaultShowAtom);

  const [show, setShow] = useState(() => {
    if (isMobileSizeViewport) {
      return false;
    }
    return defaultShowDocumentation;
  });

  const onOpen = useCallbackRef(options.onOpen);
  const onClose = useCallbackRef(options.onClose);

  const documentationResource =
    useIntegrationDocumentationResource(integration);

  const isOpen = !!(show && integration != null && documentationResource.data);

  useEffect(() => {
    if (show && documentationResource.data) {
      onOpen();
    }
  }, [show, documentationResource.data, onOpen]);

  useEffect(() => {
    if (!show) {
      onClose();
    }
  }, [show, onClose]);

  useEffect(() => {
    if (!integration) {
      onClose();
    }
  }, [integration, onClose]);

  const handleToggle = useCallback(
    (value?: boolean) => {
      setShow((prev) => {
        if (value === undefined) {
          return !prev;
        }
        return value;
      });
      setDefaultShowDocumentation((prev) => {
        if (value === undefined) {
          return !prev;
        }
        return value;
      });
    },
    [setDefaultShowDocumentation],
  );

  return {
    toggle: handleToggle,
    paneProps: {
      data: documentationResource.data,
      isLoading: documentationResource.loading,
      isOpen,
      onClose: () => {
        handleToggle(false);
      },
    },
  };
}
